import { createApi } from '@reduxjs/toolkit/query/react';

import type {
  UpdateProductCategoryRequest,
  UpdateProductBrandRequest,
  UpdateProductSupplierRequest,
  UpdateProductRequest,
  SearchProductCategoriesRequest,
  SearchProductBrandsRequest,
  SearchProductSuppliersRequest,
  GetProductCategoryByIdRequest,
  GetProductBrandByIdRequest,
  GetProductSupplierByIdRequest,
  GetProductFileRequest,
  GetProductListsRequest,
  ModelProductCategory,
  ModelProductBrand,
  ModelProductSupplier,
  ModelProduct,
  ModelProductCategoriesSearchResult,
  ModelProductBrandsSearchResult,
  ModelProductSuppliersSearchResult,
  ModelProductLists,
  GetProductFileResponse,
  GetProductByIdRequest,
  ModelProductSearchResult,
  ModelProductItemsSearchResult,
  ModelProductItem,
  GetProductItemsSearchResultRequest,
  SearchProductItemsRequest,
  SearchProductLists,
  ModelProductsSearchFilterRequest,
  GetProductsRequest,
  GetProductSuppliersRequest,
  SearchProductOperationsRequest,
  ModelProductOperationsSearchResult,
  GetProductOperationsSearchResultRequest,
  ModelProductOperationsLists,
  AcceptProductsRequest,
  GetWarehouseProductsForSaleRequest,
  GetWarehouseProductsForSaleResponse,
  ModelSelectProductsRequest,
  ModelSelectProducts,
  ModelProductsDecomissionRequest,
  SearchProductUnitsRequest,
  ModelProductUnitsSearchResult,
  GetProductUnitsSearchResultRequest,
  ModelProductUnitsLists,
  TransferProductsRequest,
  GetProductsLimitsRequest,
  ModelProductLimit,
  ModelProductsLimitsFilterData,
  ModelProductsDeletingRequest,
  FindSimilarProductsParams,
  ImportExcelRequest,
  ImportExcelResponse,
  StopImportExcelRequest,
  StopImportExcelResponse,
} from '@app/api/Products/types';
import { ProductsEndpoints as Endpoints } from '@app/api/Products/types';
import { createApiBaseQuery, Methods } from '@app/api/shared';
import type { WithActiveBranchId } from '@app/api/shared';
import { PRODUCTS_RESOURCE } from '@app/constants';

export const productsApi = createApi({
  reducerPath: 'productsApi',
  baseQuery: createApiBaseQuery(PRODUCTS_RESOURCE),
  endpoints: ({ query, mutation }) => ({
    [Endpoints.UpdateProductCategory]: mutation<
      ModelProductCategory,
      UpdateProductCategoryRequest
    >({
      query: ({ data, ActiveBranchId }) => ({
        url: Endpoints.UpdateProductCategory,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.GetProductCategoryById]: query<
      ModelProductCategory,
      GetProductCategoryByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductCategoryById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchProductCategories]: mutation<
      ModelProductCategoriesSearchResult,
      SearchProductCategoriesRequest
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: Endpoints.SearchProductCategories,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.UpdateProductBrand]: mutation<
      ModelProductBrand,
      UpdateProductBrandRequest
    >({
      query: ({ data, ActiveBranchId }) => ({
        url: Endpoints.UpdateProductBrand,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),

    [Endpoints.GetProductBrandById]: query<
      ModelProductBrand,
      GetProductBrandByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductBrandById,
        method: Methods.GET,
        params,
      }),
    }),

    [Endpoints.SearchProductBrands]: mutation<
      ModelProductBrandsSearchResult,
      SearchProductBrandsRequest
    >({
      query: ({ data, ActiveBranchId }) => ({
        url: Endpoints.SearchProductBrands,
        method: Methods.POST,
        params: { ActiveBranchId: ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.UpdateProductSupplier]: mutation<
      ModelProductSupplier,
      UpdateProductSupplierRequest
    >({
      query: ({ ActiveBranchId, data }) => ({
        url: Endpoints.UpdateProductSupplier,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.GetProductSupplierById]: query<
      ModelProductSupplier,
      GetProductSupplierByIdRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductSupplierById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchProductSuppliers]: mutation<
      ModelProductSuppliersSearchResult,
      SearchProductSuppliersRequest
    >({
      query: ({ data, ActiveBranchId }) => ({
        url: Endpoints.SearchProductSuppliers,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),

    [Endpoints.UpdateProduct]: mutation<ModelProduct, UpdateProductRequest>({
      query: ({ data, ActiveBranchId }) => ({
        url: Endpoints.UpdateProduct,
        method: Methods.POST,
        params: { ActiveBranchId },
        data,
      }),
    }),
    [Endpoints.GetProductFile]: query<
      GetProductFileResponse,
      GetProductFileRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductFile,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductLists]: query<
      ModelProductLists,
      GetProductListsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductLists,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductById]: query<ModelProduct, GetProductByIdRequest>({
      query: (params) => ({
        url: Endpoints.GetProductById,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchProducts]: mutation<
      ModelProductSearchResult,
      ModelProductsSearchFilterRequest
    >({
      query: ({ ActiveBranchId, ...data }) => ({
        url: Endpoints.SearchProducts,
        method: Methods.POST,
        data,
        params: { ActiveBranchId },
      }),
    }),
    [Endpoints.SearchProductItems]: mutation<
      ModelProductItemsSearchResult,
      SearchProductItemsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchProductItems,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.AcceptProducts]: mutation<
      ModelProductItem[],
      AcceptProductsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.AcceptProducts,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.DecomissionProducts]: mutation<
      number,
      ModelProductsDecomissionRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.DecomissionProducts,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.DeleteProducts]: mutation<number, ModelProductsDeletingRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.DeleteProducts,
        method: Methods.DELETE,
        data,
        params,
      }),
    }),
    [Endpoints.GetProductItemsSearchResult]: query<
      ModelProductItemsSearchResult,
      GetProductItemsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductItemsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductItemsLists]: query<
      SearchProductLists,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: Endpoints.GetProductItemsLists,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductsRegistryLists]: query<
      SearchProductLists,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: Endpoints.GetProductsRegistryLists,
        method: Methods.GET,
        params,
      }),
    }),

    [Endpoints.GetProducts]: query<Array<ModelProduct>, GetProductsRequest>({
      query: ({ includeIds, ...rest }) => ({
        url: Endpoints.GetProducts,
        method: Methods.GET,
        params: {
          ...rest,
          ...(includeIds?.length ? { includeIds } : {}),
        },
        paramsSerializer: { indexes: null },
      }),
    }),
    [Endpoints.GetProductSuppliers]: query<
      Array<ModelProductSupplier>,
      GetProductSuppliersRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductSuppliers,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.SearchProductOperations]: mutation<
      ModelProductOperationsSearchResult,
      SearchProductOperationsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchProductOperations,
        method: Methods.POST,
        params,
        data,
      }),
    }),
    [Endpoints.GetProductOperationsSearchResult]: query<
      ModelProductOperationsSearchResult,
      GetProductOperationsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductOperationsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductOperationsLists]: query<
      ModelProductOperationsLists,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: Endpoints.GetProductOperationsLists,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetWarehouseProductsForSelection]: mutation<
      ModelSelectProducts[],
      ModelSelectProductsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.GetWarehouseProductsForSelection,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.GetWarehouseProductsForSale]: query<
      GetWarehouseProductsForSaleResponse,
      GetWarehouseProductsForSaleRequest
    >({
      query: (params) => ({
        url: Endpoints.GetWarehouseProductsForSale,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.TransferProducts]: mutation<number, TransferProductsRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.TransferProducts,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.SearchProductUnits]: mutation<
      ModelProductUnitsSearchResult,
      SearchProductUnitsRequest
    >({
      query: ({ data, ...params }) => ({
        url: Endpoints.SearchProductUnits,
        method: Methods.POST,
        data,
        params,
      }),
    }),
    [Endpoints.GetProductUnitsSearchResult]: query<
      ModelProductUnitsSearchResult,
      GetProductUnitsSearchResultRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductUnitsSearchResult,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductUnitsLists]: query<
      ModelProductUnitsLists,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: Endpoints.GetProductUnitsLists,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductsLimits]: query<
      ModelProductLimit[],
      GetProductsLimitsRequest
    >({
      query: (params) => ({
        url: Endpoints.GetProductsLimits,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductsLimitsFilters]: query<
      ModelProductsLimitsFilterData,
      WithActiveBranchId
    >({
      query: (params) => ({
        url: Endpoints.GetProductsLimitsFilters,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.GetProductCategories]: query<
      ModelProductCategory[],
      WithActiveBranchId<{ categoryId?: number }>
    >({
      query: (params) => ({
        url: Endpoints.GetProductCategories,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.FindSimilarProducts]: query<
      ModelProduct[],
      WithActiveBranchId<FindSimilarProductsParams>
    >({
      query: (params) => ({
        url: Endpoints.FindSimilarProducts,
        method: Methods.GET,
        params,
      }),
    }),
    [Endpoints.ImportExcel]: mutation<ImportExcelResponse, ImportExcelRequest>({
      query: ({ data, ...params }) => ({
        url: Endpoints.ImportExcel,
        method: Methods.POST,
        params,
        data,
        headers: {
          'Content-Type': 'application/json',
        },
      }),
    }),
    [Endpoints.StopImportExcel]: mutation<
      StopImportExcelResponse,
      StopImportExcelRequest
    >({
      query: (params) => ({
        url: Endpoints.StopImportExcel,
        method: Methods.DELETE,
        params,
      }),
    }),
  }),
});

export const {
  useLazyGetProductFileQuery,
  useLazyGetProductListsQuery,
  useLazyGetProductBrandByIdQuery,
  useLazyGetProductCategoryByIdQuery,
  useLazyGetProductSupplierByIdQuery,
  useLazyGetProductByIdQuery,
  useLazyGetProductItemsSearchResultQuery,
  useLazyGetProductItemsListsQuery,
  useLazyGetProductsRegistryListsQuery,
  useLazyGetProductsQuery,
  useLazyGetProductSuppliersQuery,
  useLazyGetProductOperationsListsQuery,
  useLazyGetProductOperationsSearchResultQuery,
  useLazyGetProductUnitsListsQuery,
  useLazyGetProductUnitsSearchResultQuery,
  useLazyGetProductsLimitsQuery,
  useLazyGetProductsLimitsFiltersQuery,
  useLazyGetProductCategoriesQuery,
  useLazyFindSimilarProductsQuery,

  useGetProductCategoryByIdQuery,
  useGetProductBrandByIdQuery,
  useGetProductSupplierByIdQuery,
  useGetProductFileQuery,
  useGetProductListsQuery,
  useGetProductByIdQuery,
  useGetProductItemsSearchResultQuery,
  useGetProductItemsListsQuery,
  useGetProductsRegistryListsQuery,
  useGetProductsQuery,
  useGetProductSuppliersQuery,
  useGetProductOperationsSearchResultQuery,
  useGetProductOperationsListsQuery,
  useGetProductUnitsListsQuery,
  useGetProductUnitsSearchResultQuery,
  useGetProductsLimitsQuery,
  useGetProductsLimitsFiltersQuery,
  useGetProductCategoriesQuery,
  useFindSimilarProductsQuery,

  useSearchProductCategoriesMutation,
  useSearchProductBrandsMutation,
  useSearchProductSuppliersMutation,
  useUpdateProductBrandMutation,
  useUpdateProductSupplierMutation,
  useUpdateProductMutation,
  useSearchProductsMutation,
  useAcceptProductsMutation,
  useSearchProductItemsMutation,
  useDecomissionProductsMutation,
  useSearchProductOperationsMutation,
  useUpdateProductCategoryMutation,
  useGetWarehouseProductsForSelectionMutation,
  useGetWarehouseProductsForSaleQuery,
  useLazyGetWarehouseProductsForSaleQuery,
  useSearchProductUnitsMutation,
  useTransferProductsMutation,
  useDeleteProductsMutation,
  useImportExcelMutation,
  useStopImportExcelMutation,
} = productsApi;
